"use client";

import { useState } from "react";
import Image from "next/image";
import Link from "next/link";

export default function Home() {
  const [isContactOpen, setIsContactOpen] = useState(false);
  const [copiedPhone, setCopiedPhone] = useState(false);

  const EMAIL = "oli@stanley.uk.net";
  const PHONE_DISPLAY = "+44 7982822598";
  const PHONE_WHATSAPP = "447982822598";

  async function handleCopyPhone() {
    try {
      await navigator.clipboard.writeText(PHONE_DISPLAY);
      setCopiedPhone(true);
      setTimeout(() => setCopiedPhone(false), 1500);
    } catch (e) {
      console.error("copy fail", e);
    }
  }

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      {/* Navbar */}
      <header className="sticky top-0 z-30 border-b border-slate-800 bg-slate-950/80 backdrop-blur">
        <nav className="mx-auto flex max-w-5xl items-center justify-between px-4 py-3">
          <div className="text-lg font-semibold tracking-tight">
            <span className="text-sky-400">Oli</span>Sense
          </div>
          <div className="flex gap-4 text-sm">
            <a href="#home" className="hover:text-sky-400 transition-colors">
              Home
            </a>
            <a href="#about" className="hover:text-sky-400 transition-colors">
              About
            </a>
            <a href="#projects" className="hover:text-sky-400 transition-colors">
              Projects
            </a>
            <button
              type="button"
              onClick={() => setIsContactOpen(true)}
              className="hover:text-sky-400 transition-colors"
            >
              Contact
            </button>
          </div>
        </nav>
      </header>

      <main className="mx-auto max-w-5xl px-4">
        {/* Hero */}
        <section
          id="home"
          className="flex min-h-[45vh] flex-col justify-center gap-6 py-16"
        >
          <p className="text-sm font-medium uppercase tracking-[0.2em] text-sky-400">
            engineering · Prototyping · Fun
          </p>
          <h1 className="text-4xl font-semibold tracking-tight sm:text-5xl lg:text-6xl">
            Building things that{" "}
            <span className="text-sky-400">
              connect the digital and physical
            </span>
            .
          </h1>
          <p className="max-w-xl text-sm text-slate-300 sm:text-base">
            I&apos;m Oli, an engineering student who likes making hardware talk to
            software: robotic arms, 3D printers, Raspberry Pi projects, and web
            interfaces that actually do something.
          </p>
          <div className="flex flex-wrap gap-3">
            <a
              href="#projects"
              className="rounded-full border border-sky-500 bg-sky-500 px-5 py-2 text-sm font-medium text-slate-950 shadow-sm transition hover:bg-sky-400"
            >
              View projects
            </a>
            <button
              type="button"
              onClick={() => setIsContactOpen(true)}
              className="rounded-full border border-slate-600 px-5 py-2 text-sm font-medium text-slate-100 transition hover:border-sky-500 hover:text-sky-400"
            >
              Get in touch
            </button>
          </div>
        </section>

        {/* About */}
        <section id="about" className="py-16 border-t border-slate-800">
          <div className="grid gap-10 md:grid-cols-[minmax(0,260px)_minmax(0,1fr)] items-center">
            {/* Photo */}
            <div className="mx-auto">
              <div className="relative h-64 w-64 md:h-72 md:w-72 overflow-hidden rounded-2xl shadow-xl shadow-slate-900/60">
                <Image
                  src="/photos/Me.jpg"
                  alt="Oli flying a light aircraft"
                  fill
                  className="object-cover"
                />
              </div>
            </div>

            {/* Text */}
            <div className="space-y-4 text-sm sm:text-base text-slate-300">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-sky-400">
                About me
              </p>
              <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight text-slate-50">
                Engineering student building real-world systems
              </h2>
              <p>
                I&apos;m an engineering student at the University of Exeter,
                currently exploring how to combine embedded systems, mechanical
                design, and modern web technologies. I enjoy projects that
                involve real hardware: 3D printers, Raspberry Pi servers, robotic
                mechanisms, and anything that has lots of engineering tech.
              </p>
              <p>
                This site is a hub for my experiments, from custom printers with
                reinforced layers to developmental 5-axis robots.
              </p>
            </div>
          </div>
        </section>

        {/* Projects */}
        <section id="projects" className="space-y-6 border-t border-slate-800 py-16">
          <h2 className="text-2xl font-semibold tracking-tight sm:text-3xl">
            Projects
          </h2>
          <p className="max-w-2xl text-sm text-slate-300 sm:text-base">
            A few things I&apos;ve been working on recently.
          </p>

          <div className="grid gap-6 sm:grid-cols-2">
            {/* Robotic arm */}
            <Link
              href="/control"
              className="block rounded-2xl border border-slate-800 bg-slate-900/40 p-5 transition hover:-translate-y-1 hover:border-sky-500 hover:bg-slate-900/70"
            >
              <article className="flex h-full flex-col gap-3">
                <h3 className="text-lg font-semibold">Robotic arm</h3>
                <Image
                  src="/photos/arm2.jpeg"
                  alt="Robotic arm"
                  width={600}
                  height={400}
                  className="h-auto w-full rounded-xl object-cover"
                />
                <p className="text-sm text-slate-300">
                  A robotic arm you can control directly from this website, tying
                  together electronics, mechanics, and a custom web interface.
                </p>
                <p className="mt-auto text-xs uppercase tracking-wide text-slate-400">
                  Stack: Embedded · Kinematics · Control
                </p>
              </article>
            </Link>

            {/* Bionic hand */}
            <Link
              href="https://medium.com/@oli_87850/an-amateurs-approach-to-unaffordable-bionics-0a3ad44073e4"
              target="_blank"
              rel="noreferrer"
              className="block rounded-2xl border border-slate-800 bg-slate-900/40 p-5 transition hover:-translate-y-1 hover:border-sky-500 hover:bg-slate-900/70"
            >
              <article className="flex h-full flex-col gap-3">
                <h3 className="text-lg font-semibold">Bionic hand</h3>
                <Image
                  src="/photos/Hand.png"
                  alt="Bionic hand"
                  width={600}
                  height={400}
                  className="h-auto w-full rounded-xl object-cover"
                />
                <p className="text-sm text-slate-300">
                  A low-cost robotic hand built from simple components to explore
                  grasping, actuation, and control without expensive hardware.
                </p>
                <p className="mt-auto text-xs uppercase tracking-wide text-slate-400">
                  Stack: Embedded · Mechanics · Control
                </p>
              </article>
            </Link>

            {/* Metal-reinforced 3D printer */}
            <article className="flex h-full flex-col gap-3 rounded-2xl border border-slate-800 bg-slate-900/40 p-5 transition hover:-translate-y-1 hover:border-sky-500 hover:bg-slate-900/70">
              <h3 className="text-lg font-semibold">Metal-reinforced 3D printer</h3>
              <Image
                src="/photos/working.png"
                alt="Metal-reinforced 3D printer"
                width={600}
                height={400}
                className="h-auto w-full rounded-xl object-cover"
              />
              <p className="text-sm text-slate-300">
                Experiments with adding carbon fiber or metal between layers to
                boost shear strength, including custom G-code and hardware tweaks.
              </p>
              <p className="mt-auto text-xs uppercase tracking-wide text-slate-400">
                Stack: 3D printing · Control · Raspberry Pi
              </p>
            </article>

            {/* CMG robot */}
            <Link
              href="/Gyro"
              className="block rounded-2xl border border-slate-800 bg-slate-900/40 p-5 transition hover:-translate-y-1 hover:border-sky-500 hover:bg-slate-900/70"
            >
              <article className="flex h-full flex-col gap-3">
                <h3 className="text-lg font-semibold">
                  Control Moment Gyro inline balancing robot
                </h3>
                <Image
                  src="/photos/Gyro.jpeg"
                  alt="Control Moment Gyro balancing robot"
                  width={600}
                  height={400}
                  className="h-auto w-full rounded-xl object-cover"
                />
                <p className="text-sm text-slate-300">
                  A fun project involving PID controllers and mechanical systems.
                </p>
                <p className="mt-auto text-xs uppercase tracking-wide text-slate-400">
                  Stack: Mechanical Design · CMG · PID controllers
                </p>
              </article>
            </Link>
            {/* Algorithmic Trading */}
            <Link
              href="/trading"
              className="block rounded-2xl border border-slate-800 bg-slate-900/40 p-5 transition hover:-translate-y-1 hover:border-sky-500 hover:bg-slate-900/70"
            >
              <article className="flex h-full flex-col gap-3">
                <h3 className="text-lg font-semibold">Algorithmic Trading</h3>
                {/* Mini trading terminal */}
                <div className="relative h-48 w-full overflow-hidden rounded-xl bg-[#0d1117] font-mono text-[10px]">
                  {/* Terminal header bar */}
                  <div className="flex items-center justify-between border-b border-slate-700/60 px-3 py-1.5">
                    <div className="flex items-center gap-2">
                      <span className="text-slate-300 font-semibold tracking-wider">DEFENCE·KF</span>
                      <span className="text-emerald-400">▲ +5.55pp</span>
                    </div>
                    <span className="rounded bg-amber-500/20 px-1.5 py-0.5 text-amber-400">PRIVATE</span>
                  </div>

                  {/* Chart area */}
                  <div className="relative px-3 pt-2 pb-0">
                    <svg viewBox="0 0 280 80" className="w-full" preserveAspectRatio="none">
                      <defs>
                        <linearGradient id="chartFill" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#38bdf8" stopOpacity="0.15" />
                          <stop offset="100%" stopColor="#38bdf8" stopOpacity="0" />
                        </linearGradient>
                        <linearGradient id="bhFill" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#64748b" stopOpacity="0.1" />
                          <stop offset="100%" stopColor="#64748b" stopOpacity="0" />
                        </linearGradient>
                      </defs>

                      {/* Grid lines */}
                      {[20, 40, 60].map(y => (
                        <line key={y} x1="0" y1={y} x2="280" y2={y} stroke="#1e293b" strokeWidth="1" />
                      ))}

                      {/* Buy-and-hold (grey, behind) */}
                      <polyline
                        points="0,30 28,33 56,38 84,43 112,50 140,55 168,62 196,66 224,70 252,72 280,74"
                        fill="url(#bhFill)" stroke="#475569" strokeWidth="1" strokeDasharray="3 2"
                      />

                      {/* Strategy equity curve (sky blue, above) */}
                      <polyline
                        points="0,30 28,30 56,32 84,30 112,32 140,34 168,34 196,36 224,40 252,44 280,46"
                        fill="url(#chartFill)" stroke="#38bdf8" strokeWidth="1.5"
                      />

                      {/* Entry markers */}
                      {[56, 140, 224].map(x => (
                        <circle key={x} cx={x} cy={x === 56 ? 32 : x === 140 ? 34 : 40} r="2.5" fill="#34d399" />
                      ))}
                    </svg>
                  </div>

                  {/* Legend */}
                  <div className="absolute bottom-2 left-3 flex items-center gap-3">
                    <span className="flex items-center gap-1 text-sky-400">
                      <span className="inline-block h-px w-4 bg-sky-400"></span>Strategy
                    </span>
                    <span className="flex items-center gap-1 text-slate-500">
                      <span className="inline-block h-px w-4 border-t border-dashed border-slate-500"></span>B&amp;H
                    </span>
                    <span className="flex items-center gap-1 text-emerald-400">
                      <span className="inline-block h-2 w-2 rounded-full bg-emerald-400"></span>Entry
                    </span>
                  </div>

                  {/* Stats strip */}
                  <div className="absolute bottom-2 right-3 flex items-center gap-3 text-slate-400">
                    <span>SHP <span className="text-slate-300">0.847</span></span>
                    <span>DD <span className="text-red-400">−12.7%</span></span>
                  </div>
                </div>
                <p className="text-sm text-slate-300">
                  An adaptive Kalman filter state estimation strategy on stocks, with live paper-trading.
                </p>
                <p className="mt-auto text-xs uppercase tracking-wide text-slate-400">
                  Stack: Python · Quant Finance · Signal Processing
                </p>
              </article>
            </Link>
            {/* Copy Trading */}
            <Link
              href="/copytrade"
              className="block rounded-2xl border border-slate-800 bg-slate-900/40 p-5 transition hover:-translate-y-1 hover:border-sky-500 hover:bg-slate-900/70"
            >
              <article className="flex h-full flex-col gap-3">
                <h3 className="text-lg font-semibold">Wallet Copy-Trading Bot</h3>
                <div className="relative h-48 w-full overflow-hidden rounded-xl bg-[#0d1117] font-mono text-[10px] flex flex-col justify-between p-3">
                  <div className="flex items-center justify-between">
                    <span className="text-slate-300 font-semibold tracking-wider">ON-CHAIN·MIRROR</span>
                    <span className="rounded bg-amber-500/20 px-1.5 py-0.5 text-amber-400">PRIVATE</span>
                  </div>
                  <div className="space-y-1.5">
                    <div className="flex items-center gap-2 text-emerald-400">
                      <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
                      BUY 0.0412 ETH/USD
                    </div>
                    <div className="flex items-center gap-2 text-slate-500">
                      <span className="h-1.5 w-1.5 rounded-full bg-slate-600" />
                      SKIPPED — below min transfer
                    </div>
                    <div className="flex items-center gap-2 text-red-400">
                      <span className="h-1.5 w-1.5 rounded-full bg-red-400" />
                      SELL 0.0180 LINK/USD
                    </div>
                  </div>
                  <div className="flex items-center gap-3 text-slate-500">
                    <span>wallets <span className="text-slate-300">tracked</span></span>
                    <span>spot only</span>
                  </div>
                </div>
                <p className="text-sm text-slate-300">
                  Mirrors curated Ethereum wallets&apos; trades as small, fixed-risk positions via Alpaca paper trading.
                </p>
                <p className="mt-auto text-xs uppercase tracking-wide text-slate-400">
                  Stack: Python · Etherscan API · Alpaca API
                </p>
              </article>
            </Link>
          </div>
        </section>

        {/* Contact section (optional static info) */}
        <section id="contact" className="space-y-4 py-16 border-t border-slate-800">
          <h2 className="text-2xl font-semibold tracking-tight sm:text-3xl">
            Contact
          </h2>
          <p className="max-w-2xl text-sm text-slate-300 sm:text-base">
            Want to talk about a project, collaboration, or idea? Feel free to
            reach out.
          </p>
          <div className="rounded-2xl border border-slate-800 bg-slate-900/40 p-5 text-sm text-slate-200">
            <p>
              Email:{" "}
              <a
                href={`mailto:${EMAIL}`}
                className="text-sky-400 underline underline-offset-4 hover:text-sky-300"
              >
                {EMAIL}
              </a>
            </p>
          </div>
        </section>

        {/* Contact modal */}
        {isContactOpen && (
          <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/60">
            <div className="w-full max-w-sm rounded-2xl border border-slate-700 bg-slate-900 p-6 shadow-xl">
              <div className="mb-4 flex items-center justify-between">
                <h2 className="text-lg font-semibold">Contact</h2>
                <button
                  type="button"
                  onClick={() => setIsContactOpen(false)}
                  className="text-slate-400 hover:text-slate-200"
                >
                  ✕
                </button>
              </div>

              <div className="space-y-4 text-sm">
                {/* Email block */}
                <div>
                  <p className="mb-1 text-slate-400">Email</p>
                  <p className="break-all font-mono text-slate-100">{EMAIL}</p>

                  <a
                    href={`mailto:${EMAIL}`}
                    className="mt-2 inline-flex items-center rounded-md border border-sky-500 px-3 py-1 text-xs font-medium text-sky-100 hover:bg-sky-500/10"
                  >
                    Take me to email
                  </a>
                </div>

                {/* Phone / WhatsApp block */}
                <div className="border-t border-slate-800 pt-4">
                  <p className="mb-1 text-slate-400">Phone / WhatsApp</p>
                  <p className="font-mono text-slate-100">{PHONE_DISPLAY}</p>

                  <div className="mt-2 flex items-center gap-3">
                    <a
                      href={`https://wa.me/${PHONE_WHATSAPP}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center rounded-md border border-emerald-500 px-3 py-1 text-xs font-medium text-emerald-100 hover:bg-emerald-500/10"
                    >
                      Open in WhatsApp
                    </a>

                    <button
                      type="button"
                      onClick={handleCopyPhone}
                      className="rounded-md border border-sky-500 px-3 py-1 text-xs font-medium text-sky-100 hover:bg-sky-500/10"
                    >
                      Copy phone
                    </button>

                    {copiedPhone && (
                      <span className="text-xs text-emerald-400">Copied</span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Floating quick link to Control */}
      <Link
        href="/control"
        aria-label="Go to Control page"
        className="fixed bottom-6 right-6 z-50 inline-flex items-center gap-2 rounded-full border border-sky-500 bg-slate-950/90 px-4 py-3 text-sm font-semibold text-sky-200 shadow-lg shadow-black/40 backdrop-blur transition hover:bg-slate-900 hover:text-sky-100 hover:border-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:ring-offset-2 focus:ring-offset-slate-950"
      >
        <span className="text-base leading-none">🎮</span>
        Control
      </Link>

      {/* Footer */}
      <footer className="border-t border-slate-800 py-6">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-4 text-xs text-slate-500">
          <span>© {new Date().getFullYear()} OliSense.</span>
          <span>Built by Oliver Stanley · Hosted on Raspberry Pi</span>
        </div>
      </footer>
    </div>
  );
}
